import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS, DARK_HERO_PATHS, CONTACT } from '../content/data';
import { useLead } from './LeadModal';
import { useContent } from '../lib/content';
import { EASE } from './motion';

function NavLink({ l, onDark, pathname }) {
  const cls = `text-[13px] font-semibold tracking-tight transition-colors duration-300 ${
    onDark ? 'text-white/75 hover:text-gold' : 'text-navy/70 hover:text-electric'
  } ${pathname === l.to ? (onDark ? '!text-gold' : '!text-electric') : ''}`;
  if (l.external) {
    return <a data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} href={l.to} target="_blank" rel="noreferrer" className={cls}>{l.label}</a>;
  }
  return <Link data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} to={l.to} className={cls}>{l.label}</Link>;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const { pathname } = useLocation();
  const { open } = useLead();
  const { settings, links } = useContent();
  const darkHero = DARK_HERO_PATHS.includes(pathname);
  const onDark = darkHero && !scrolled && !drawer;
  const ctaLabel = settings.navCtaLabel || 'Apply / Enquire';
  const extraLinks = links.filter((l) => l.location === 'header').map((l) => ({ label: l.label, to: l.url, external: /^https?:\/\//.test(l.url) }));
  const allLinks = [...NAV_LINKS, ...extraLinks];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => setDrawer(false), [pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,border-color,backdrop-filter] duration-500 ${
          onDark ? 'border-transparent bg-transparent' : 'border-b border-navy/8 bg-white/85 backdrop-blur-xl'
        }`}
      >
        <div className="wrap flex items-center justify-between py-4">
          <Link to="/" data-testid="nav-logo" className="flex touch-target items-center">
            <img
              src={onDark ? '/assets/logolight.webp' : '/assets/logodark.webp'}
              alt="DITEC — Digital Intelligence & Technology Excellence Council"
              className="h-20 w-auto sm:h-24"
            />
          </Link>
          <nav className="hidden items-center gap-7 lg:flex">
            {allLinks.map((l) => (
              <NavLink key={l.to + l.label} l={l} onDark={onDark} pathname={pathname} />
            ))}
            <button
              data-testid="nav-cta-button"
              onClick={() => open()}
              className={`rounded-full px-6 py-2.5 text-[13px] font-semibold transition-colors duration-300 ${
                onDark ? 'bg-gold text-navy hover:bg-white' : 'bg-electric text-white hover:bg-navy'
              }`}
            >
              {ctaLabel}
            </button>
          </nav>
          <button
            data-testid="nav-menu-button"
            onClick={() => setDrawer(true)}
            aria-label="Open menu"
            className={`flex touch-target items-center justify-center lg:hidden ${onDark ? 'text-white' : 'text-navy'}`}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {drawer && (
          <motion.div data-testid="mobile-drawer" className="fixed inset-0 z-[80]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" onClick={() => setDrawer(false)} />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.45, ease: EASE }}
              className="bg-grid-dark absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-navy p-6"
            >
              <div className="flex items-center justify-between">
                <img src="/assets/logolight.webp" alt="DITEC" className="h-12 w-auto" />
                <button
                  data-testid="drawer-close-button"
                  onClick={() => setDrawer(false)}
                  aria-label="Close menu"
                  className="flex touch-target items-center justify-center text-white/70 hover:text-gold"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <nav className="mt-10 flex flex-col gap-1">
                {allLinks.map((l, i) => (
                  <motion.div key={l.to + l.label} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.05 }}>
                    {l.external ? (
                      <a
                        data-testid={`drawer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                        href={l.to}
                        target="_blank"
                        rel="noreferrer"
                        className="flex touch-target items-center justify-between border-b border-white/10 py-4 text-lg font-semibold tracking-tight text-white transition-colors hover:text-gold"
                      >
                        {l.label}
                        <span className="font-mono text-xs text-gold/50">0{i + 1}</span>
                      </a>
                    ) : (
                      <Link
                        data-testid={`drawer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                        to={l.to}
                        className="flex touch-target items-center justify-between border-b border-white/10 py-4 text-lg font-semibold tracking-tight text-white transition-colors hover:text-gold"
                      >
                        {l.label}
                        <span className="font-mono text-xs text-gold/50">0{i + 1}</span>
                      </Link>
                    )}
                  </motion.div>
                ))}
              </nav>
              <div className="mt-auto space-y-4">
                <button
                  data-testid="drawer-cta-button"
                  onClick={() => { setDrawer(false); open(); }}
                  className="w-full touch-target rounded-full bg-gold py-4 text-sm font-semibold text-navy transition-colors hover:bg-white"
                >
                  {ctaLabel}
                </button>
                <p className="text-center font-mono text-xs text-white/40">{CONTACT.email} · {CONTACT.phone}</p>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function StickyCTA() {
  const { open } = useLead();
  return (
    <div data-testid="sticky-bottom-cta" className="fixed inset-x-0 bottom-0 z-[60] border-t border-white/10 bg-navy/90 p-3 backdrop-blur-xl md:hidden">
      <button
        data-testid="sticky-cta-button"
        onClick={() => open()}
        className="w-full touch-target rounded-full bg-gold py-3.5 text-sm font-semibold text-navy transition-colors active:bg-white"
      >
        Apply / Enquire — Start the Conversation
      </button>
    </div>
  );
}
